import React, { MouseEventHandler, useMemo } from 'react';
import { useHideOverflowing } from 'components/common/useHideOverflowing';
import { useTranslation } from 'react-i18next';
import {
  Destination,
  DestinationType,
  Organization,
} from 'components/common/ObjectTypes';
import Pagination from 'components/common/Pagination';
import GlobalSearch from 'components/common/GlobalSearch';
import {
  listPageClasses,
  buttonClasses,
  filterClasses,
  paginationClasses,
  skeletonClasses,
} from 'components/common/tailwindClassnames';
import {
  CopyToClipboard,
  RefreshSpinner,
} from 'components/common/ProjectIcons';
import NoResults from 'components/common/NoResults';
import SelectInput from 'components/common/SelectInput';
import SortByDropdown, { SortOption } from 'components/common/SortByDropdown';
import { toast } from 'react-toastify';

type DestinationsListProps = {
  canAddDestination: boolean;
  roleFilterValue: { label: string; value: string } | null;
  onChangeRole: (selected: { label: string; value: string } | null) => void;
  roleFilterDisabled?: boolean;
  reloadDestinations: boolean;
  destinations: Destination[];
  destinationTypes: DestinationType[];
  loadingDestinationTypes: boolean;
  destinationUrls: string[];
  loadingDestinationUrls: boolean;
  organizations: Organization[];
  loadingOrganizations: boolean;
  onAddDestination: MouseEventHandler;
  onViewDetails: Function;
  onChangeGlobalSearch: (value: string) => void;
  onRefresh: () => void;
  globalSearchStr: string;
  ordering: string;
  onChangeSortBy: (selected: SortOption) => void;
  onChangeEndpoint: Function;
  endpointFilterValue: any;
  onChangeType: Function;
  typeFilterValue: any;
  onChangeOwner: Function;
  ownerFilterValue: any;
  onPageClicked: Function;
  totalCount: number;
  pageLoaded: boolean;
  take: number;
  offset: number;
};

const DestinationsList = ({
  canAddDestination,
  roleFilterValue,
  onChangeRole,
  roleFilterDisabled,
  reloadDestinations,
  destinations,
  destinationTypes,
  loadingDestinationTypes,
  destinationUrls,
  loadingDestinationUrls,
  organizations,
  loadingOrganizations,
  onAddDestination,
  onViewDetails,
  onChangeGlobalSearch,
  onRefresh,
  globalSearchStr,
  ordering,
  onChangeSortBy,
  onChangeEndpoint,
  endpointFilterValue,
  onChangeType,
  typeFilterValue,
  onChangeOwner,
  ownerFilterValue,
  onPageClicked,
  totalCount,
  pageLoaded,
  take,
  offset,
}: DestinationsListProps) => {
  // Phones: a control that does not fully fit in its row is hidden, not cut in half.
  const searchRowRef = useHideOverflowing<HTMLDivElement>();
  const filterRowRef = useHideOverflowing<HTMLDivElement>();
  const { t } = useTranslation();

  const sortOptions: SortOption[] = useMemo(
    () => [
      { label: t('Name (A-Z)'), value: 'name' },
      { label: t('Name (Z-A)'), value: '-name' },
      { label: t('Workspace'), value: 'owner__name' },
      { label: t('Technology'), value: 'type__name' },
    ],
    [t],
  );

  const hasActiveFilters = !!(
    globalSearchStr ||
    endpointFilterValue ||
    typeFilterValue ||
    ownerFilterValue
  );

  const clearAllFilters = () => {
    onChangeGlobalSearch('');
    onChangeEndpoint(null);
    onChangeType(null);
    onChangeOwner(null);
  };

  return (
    <div className="transition-all duration-300">
      <div className={listPageClasses.band}>
        <div className={listPageClasses.bandRow}>
          <h1
            data-testid="destinations_page_header"
            className={listPageClasses.title}
          >
            {t('Connections')}
          </h1>
          {canAddDestination && (
            <button
              className={buttonClasses.primary}
              onClick={onAddDestination}
            >
              {t('Create Connection')}
            </button>
          )}
        </div>
      </div>
      <div className={listPageClasses.body}>
        <div className="mx-auto max-w-list-table">
          {/* Search row */}
          {/* Never wraps: on a phone what does not fit is clipped, not dropped to its own line. */}
          <div
            ref={searchRowRef}
            className="mb-3 flex flex-row flex-nowrap items-end gap-3 overflow-x-clip"
          >
            <GlobalSearch
              onChangeGlobalSearch={onChangeGlobalSearch}
              placeholder={t('Search')}
              savedString={globalSearchStr}
            />
            <SortByDropdown
              options={sortOptions}
              ordering={ordering}
              onChangeSortBy={onChangeSortBy}
            />
            <button
              type="button"
              onClick={() => onRefresh()}
              aria-label={t('Refresh list')}
              disabled={reloadDestinations}
              className={`mb-2 ml-auto flex h-[38px] w-8 shrink-0 cursor-pointer items-center justify-center self-end ${reloadDestinations ? 'origin-center animate-spin' : ''} transition-colors duration-300`}
            >
              <RefreshSpinner />
            </button>
            {hasActiveFilters && (
              <button
                className={`${buttonClasses.clearFilters} mb-2 whitespace-nowrap`}
                onClick={clearAllFilters}
              >
                {t('Clear All Filters')}
              </button>
            )}
          </div>
          {/* Filter dropdowns row */}
          <div
            ref={filterRowRef}
            className="mb-3 flex flex-row flex-nowrap gap-3 overflow-x-clip"
          >
            {/* Technology */}
            <div className="flex shrink-0 flex-col gap-1">
              <span className={filterClasses.label}>{t('Technology')}</span>
              <SelectInput
                name="type__in"
                options={destinationTypes.map((d) => ({
                  label: d.name,
                  value: d.id,
                }))}
                value={typeFilterValue}
                onChange={onChangeType}
                isMulti={false}
                isClearable={true}
                isSearchable={true}
                isDisabled={false}
                isLoading={loadingDestinationTypes}
                className="w-48"
                variant="routeFilter"
              />
            </div>
            {/* URL */}
            <div className="flex shrink-0 flex-col gap-1">
              <span className={filterClasses.label}>{t('URL')}</span>
              <SelectInput
                name="base_url__in"
                options={destinationUrls.map((url) => {
                  let hostname = url;
                  try {
                    hostname = new URL(url).hostname;
                  } catch {}
                  return { label: hostname, value: url };
                })}
                value={endpointFilterValue}
                onChange={onChangeEndpoint}
                isMulti={false}
                isClearable={true}
                isSearchable={true}
                isDisabled={false}
                isLoading={loadingDestinationUrls}
                className="w-48"
                variant="routeFilter"
              />
            </div>
            {/* Role — derived from the Routes list, filtered in the browser */}
            <div className="flex shrink-0 flex-col gap-1">
              <span className={filterClasses.label}>{t('Role')}</span>
              <SelectInput
                name="role"
                options={[
                  { label: t('Used as a source'), value: 'source' },
                  { label: t('Used as a destination'), value: 'destination' },
                ]}
                value={roleFilterValue}
                onChange={onChangeRole}
                isMulti={false}
                isClearable={true}
                isSearchable={false}
                isDisabled={!!roleFilterDisabled}
                isLoading={!!roleFilterDisabled}
                className="w-48"
                variant="routeFilter"
              />
            </div>
            {/* Workspace */}
            <div className="flex shrink-0 flex-col gap-1">
              <span className={filterClasses.label}>{t('Workspace')}</span>
              <SelectInput
                name="owner__in"
                options={organizations.map((org) => ({
                  label: org.name,
                  value: org.id,
                }))}
                value={ownerFilterValue}
                onChange={onChangeOwner}
                isMulti={false}
                isClearable={true}
                isSearchable={true}
                isDisabled={false}
                isLoading={loadingOrganizations}
                className="w-48"
                variant="routeFilter"
              />
            </div>
          </div>
          {/* Cards */}
          <div className="flex w-full flex-col gap-2">
            {!pageLoaded || reloadDestinations ? (
              [...Array(8)].map((_, i) => (
                <div
                  key={i}
                  className="rounded-lg border border-teal-t30 bg-white p-4"
                  data-testid="loading"
                >
                  {/* Title row: name */}
                  <div className={`mb-3 h-5 w-48 ${skeletonClasses.base}`} />
                  {/* Content row */}
                  <div className="flex flex-col gap-6 min-[780px]:flex-row min-[780px]:items-start min-[780px]:gap-12">
                    <div className="flex min-w-0 flex-1 flex-col gap-1">
                      <div className={`h-4 w-32 ${skeletonClasses.base}`} />
                      <div className={`h-3 w-40 ${skeletonClasses.base}`} />
                    </div>
                    <div
                      className={`h-4 w-28 min-[780px]:ml-auto min-[780px]:mr-8 ${skeletonClasses.base}`}
                    />
                  </div>
                </div>
              ))
            ) : destinations.length === 0 ? (
              <div className="rounded-lg border border-teal-t30 bg-white py-16">
                <NoResults
                  headerMsg={t('No Results Found')}
                  labelMsg={t(
                    'You may need to adjust your search or filters to find what you are looking for',
                  )}
                />
              </div>
            ) : (
              destinations.map((destination: Destination) => {
                let dest_url: string;
                try {
                  dest_url = new URL(destination.base_url).hostname;
                } catch (err) {
                  dest_url = '';
                }
                const navigateToDestination = (e: React.MouseEvent) => {
                  if (e.metaKey || e.ctrlKey) {
                    window.open(
                      `/connections/${destination.id}/configuration`,
                      '_blank',
                      'noopener,noreferrer',
                    );
                  } else {
                    onViewDetails(destination.id);
                  }
                };
                return (
                  <div
                    key={destination.id}
                    className="cursor-pointer rounded-lg border border-teal-t30 bg-white p-4"
                    role="button"
                    tabIndex={0}
                    onClick={navigateToDestination}
                    onKeyDown={(e: React.KeyboardEvent) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onViewDetails(destination.id);
                      }
                    }}
                  >
                    {/* Title row: system name */}
                    <div className="mb-3">
                      <span
                        className="block truncate text-[18px] font-medium text-green-1"
                        title={destination.name}
                      >
                        {destination.name}
                      </span>
                    </div>
                    {/* Content row: technology + url | workspace.
                                    NOTE for reviewers: the wide gap-12 here is intentional (gives the
                                    right-aligned workspace breathing room) and deliberately differs from
                                    the routes-list card spacing — not an inconsistency to flag. */}
                    <div className="flex flex-col gap-6 min-[780px]:flex-row min-[780px]:items-start min-[780px]:gap-12">
                      {/* Technology + URL */}
                      <div className="flex min-w-0 flex-1 flex-col">
                        <span className="text-base font-normal text-ui-text-primary-text">
                          {destination.type.name}
                        </span>
                        {destination.base_url && (
                          <span className="flex min-w-0 items-center gap-2 text-xs text-brand-accent-green">
                            <span
                              className="truncate"
                              title={destination.base_url}
                            >
                              {dest_url}
                            </span>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigator.clipboard.writeText(
                                  destination.base_url,
                                );
                                toast.info(
                                  `${t('URL')} ${t('copied to clipboard')}`,
                                  {
                                    position: toast.POSITION.TOP_RIGHT,
                                    autoClose: 2000,
                                  },
                                );
                              }}
                              className="flex flex-shrink-0 items-center justify-center opacity-50 transition-opacity hover:opacity-100"
                              aria-label={t('Copy URL to clipboard')}
                            >
                              <CopyToClipboard />
                            </button>
                          </span>
                        )}
                      </div>
                      {/* Workspace */}
                      <div className="min-w-0 flex-shrink-0 min-[780px]:mr-8 min-[780px]:max-w-[50%] min-[780px]:text-right">
                        <span
                          className="block truncate text-sm text-ui-text-primary-text"
                          title={destination.owner.name}
                        >
                          {destination.owner.name}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
            {totalCount > 0 && (
              <Pagination
                showText={true}
                handlePageClick={onPageClicked}
                pageCount={Math.ceil(totalCount / take)}
                resultStart={offset + 1}
                resultEnd={
                  offset + take < totalCount ? offset + take : totalCount
                }
                totalCount={totalCount}
                initialPage={Math.floor(offset / take)}
                take={take}
                loading={!pageLoaded}
                mainclassname={paginationClasses.container}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DestinationsList;
